import headphonesIcon from 'Assets/images/headphones-icon.svg';
import micIcon from 'Assets/images/mic-icon.svg';
import { ErrorBanner } from 'Components/shared/ErrorBanner';
import { Heading } from 'Components/shared/Heading';
import { SettingsDropdown } from 'Components/shared/SettingsDropDown';
import { TextButton } from 'Components/shared/TextButton';
import { Switch } from '~/lib/ui/switch';
import { observer } from 'mobx-react';
import { useAudioSettings } from 'Modules/softphone/hooks';
import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Grid } from 'semantic-ui-react';
import { stopAllStreams } from 'Utils/streamUtils';

/**
 * react-mic builds an AudioContext in its module body, so importing it opens
 * an audio session. The settings routes are part of the app shell, which made
 * that happen on every page load — reloading the desktop app was enough to
 * disturb whatever else the machine was playing (CUU2-3411). Importing it only
 * when the user turns the test on keeps it out of the load path.
 */
const ReactMic = lazy(async () => {
  const { ReactMic: Component } = (await import('react-mic')) as {
    ReactMic: React.ComponentType<{
      record: boolean;
      className?: string;
      strokeColor?: string;
      onStop?: () => void;
    }>;
  };

  return { default: Component };
});

/** Holds the row's height while react-mic is not loaded. */
const SoundwavesPlaceholder = () => (
  <canvas className="soundwaves" width={640} height={100} aria-hidden />
);

/**
 * AudioVideoSettings Component
 *
 * Manages audio device configuration (microphone, speakers, ringtone).
 * Automatically works with both legacy (MobX) and new (Zustand) softphone
 * through the useAudioSettings hook.
 *
 * The hook handles all the complexity of switching between implementations,
 * this component just renders the UI.
 *
 * Note: This component is wrapped with observer() to react to MobX observable
 * changes when using the legacy softphone.
 */
export const AudioVideoSettings: React.FC = observer(() => {
  const testid = 'audioVideoSettings';

  const {
    microphoneOptions,
    speakerOptions,
    ringerOptions,
    selectedMicrophone,
    selectedSpeaker,
    selectedRinger,
    handleMicrophoneChange,
    handleSpeakerChange,
    handleRingerChange,
    handleRefreshClick,
    toggleTestMicrophone,
    permissionDenied,
    testMicrophoneEnabled,
  } = useAudioSettings();

  // Loaded on first use and kept mounted from then on, so react-mic still sees
  // `record` go false and releases the microphone as it always has.
  const [microphoneTestLoaded, setMicrophoneTestLoaded] = useState(false);

  useEffect(() => {
    if (testMicrophoneEnabled) {
      setMicrophoneTestLoaded(true);
    }
  }, [testMicrophoneEnabled]);

  // Safari fallback: add default option if no devices found
  const allOptionsMic =
    microphoneOptions.length < 2
      ? [
          {
            key: 'default',
            text: 'System Default Microphone Device',
            value: 'default',
          },
          ...microphoneOptions,
        ]
      : microphoneOptions;

  const allOptionsSpeakers =
    speakerOptions.length === 0
      ? [
          {
            key: 'default',
            text: 'System Default Speaker Device',
            value: 'default',
          },
          ...speakerOptions,
        ]
      : speakerOptions;

  const allOptionsRingtoneSpeakers =
    ringerOptions.length === 0
      ? [
          {
            key: 'default',
            text: 'System Default Ringtone Device',
            value: 'default',
          },
          ...ringerOptions,
        ]
      : ringerOptions;

  // Find selected values for display
  const defaultMicValue = allOptionsMic.find(
    (item) => item.value === selectedMicrophone
  )?.value;

  const defaultSoundValue = allOptionsSpeakers.find(
    (item) => item.value === selectedSpeaker
  )?.value;

  const defaultRingToneValue = allOptionsRingtoneSpeakers.find(
    (item) => item.value === selectedRinger
  )?.value;

  return (
    <Grid.Column>
      <Heading variant="h2">Audio</Heading>
      <SettingsDropdown
        label="Microphone"
        options={allOptionsMic}
        onChange={handleMicrophoneChange}
        value={defaultMicValue}
        icon={micIcon}
        testid={`${testid}-dropdownMicrophone`}
      />
      {permissionDenied ? (
        <ErrorBanner
          show
          content="To make and receive calls, enable microphone access in your browser settings."
          action={
            <TextButton
              variant="inverted"
              testid={`${testid}-errorBanner-buttonRefresh`}
              onClick={handleRefreshClick}
            >
              Refresh
            </TextButton>
          }
        />
      ) : (
        <div className="settings-select semantic-flex-column">
          <span className="sub-headers settings-select--label">
            Test Microphone
          </span>
          <div className="semantic-flex-align-items-center semantic-flex-row">
            <Switch
              onCheckedChange={toggleTestMicrophone}
              checked={testMicrophoneEnabled}
              data-testid={`${testid}-toggleButtonTestMicrophone`}
              aria-label='Test Microphone'
              className='mr-10 scale-125'
            />
            {microphoneTestLoaded ? (
              <Suspense fallback={<SoundwavesPlaceholder />}>
                <ReactMic
                  record={testMicrophoneEnabled}
                  className="soundwaves"
                  strokeColor="#0099AD"
                  onStop={() => stopAllStreams()}
                />
              </Suspense>
            ) : (
              <SoundwavesPlaceholder />
            )}
          </div>
        </div>
      )}
      <SettingsDropdown
        label="Speakers"
        options={allOptionsSpeakers}
        onChange={handleSpeakerChange}
        value={defaultSoundValue}
        icon={headphonesIcon}
        testid={`${testid}-dropdownSpeakers`}
      />
      <SettingsDropdown
        label="Ring options"
        options={allOptionsRingtoneSpeakers}
        onChange={handleRingerChange}
        value={defaultRingToneValue}
        icon={headphonesIcon}
        testid={`${testid}-dropdownRingOptions`}
      />
    </Grid.Column>
  );
});
